/**
 * 效果 #118-120：火焰效果、复古怀旧滤镜、形态变换增强版
 * 创建时间：2026-03-01
 */

import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
  random,
} from 'remotion';

// ============================================================================
// #118 火焰效果
// ============================================================================

interface FlameParticleProps {
  delay: number;
  x: number;
  baseY: number;
  color: string;
  size: number;
}

const FlameParticle: React.FC<FlameParticleProps> = ({
  delay,
  x,
  baseY,
  color,
  size,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  
  const progress = spring({
    frame: frame - delay,
    fps,
    config: {
      damping: 20,
      stiffness: 100,
    },
  });
  
  const opacity = interpolate(progress, [0, 0.3, 0.7, 1], [0, 1, 1, 0]);
  const y = baseY - progress * 400;
  const scale = interpolate(progress, [0, 0.5, 1], [1, 1.5, 0.3]);
  const rotation = interpolate(progress, [0, 1], [0, 45]);
  
  return (
    <div
      style={{
        position: 'absolute',
        left: x,
        bottom: y,
        width: size,
        height: size * 1.5,
        background: color,
        borderRadius: '50% 50% 50% 50% / 60% 60% 40% 40%',
        opacity,
        transform: `scale(${scale}) rotate(${rotation}deg)`,
        filter: 'blur(3px)',
        boxShadow: `0 0 ${size}px ${color}`,
      }}
    />
  );
};

export const FlameEffect: React.FC<{
  text?: string;
  color?: string;
  backgroundColor?: string;
}> = ({
  text = 'FIRE',
  color = '#ff6b35',
  backgroundColor = '#1a1a1a',
}) => {
  const frame = useCurrentFrame();
  const { width, height, fps } = useVideoConfig();
  
  const particles = Array.from({ length: 60 }, (_, i) => {
    const colors = ['#ff6b35', '#ff8c42', '#ffd700'];
    return {
      delay: i * 2,
      x: width / 2 + random(`x-${i}`) * 200 - 100,
      baseY: height / 2 + 100,
      color: colors[i % 3],
      size: interpolate(random(`size-${i}`), [0, 1], [20, 60]),
    };
  });
  
  const textScale = spring({
    frame,
    fps,
    config: { damping: 15, stiffness: 100 },
  });
  
  return (
    <AbsoluteFill style={{ background: backgroundColor }}>
      {/* 火焰粒子 */}
      {particles.map((particle, i) => (
        <FlameParticle key={i} {...particle} />
      ))}
      
      {/* 发光背景 */}
      <div
        style={{
          position: 'absolute',
          left: '50%',
          bottom: height / 2 + 50,
          width: 400,
          height: 200,
          transform: 'translateX(-50%)',
          background: 'radial-gradient(ellipse at center, rgba(255,107,53,0.4) 0%, transparent 70%)',
          filter: 'blur(30px)',
        }}
      />
      
      {/* 文字 */}
      <div
        style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: `translate(-50%, -50%) scale(${textScale})`,
          fontSize: 150,
          fontWeight: 'bold',
          color: color,
          textShadow: `
            0 0 10px ${color},
            0 0 20px ${color},
            0 0 40px ${color},
            0 0 60px rgba(255,107,53,0.8)
          `,
          fontFamily: 'Arial, sans-serif',
        }}
      >
        {text}
      </div>
    </AbsoluteFill>
  );
};

// ============================================================================
// #119 复古怀旧滤镜效果
// ============================================================================

export const RetroVintageFilter: React.FC<{
  text?: string;
  filterType?: '80s' | '90s' | 'vhs';
  color?: string;
}> = ({
  text = 'RETRO',
  filterType = '80s',
  color = '#ff6b9d',
}) => {
  const frame = useCurrentFrame();
  const { width, height, fps } = useVideoConfig();
  
  const textProgress = spring({
    frame,
    fps,
    config: { damping: 15 },
  });
  
  // VHS 故障效果
  const vhsOffset = Math.sin(frame * 0.5) * 3;
  const scanlineY = (frame * 5) % height;
  
  // 80s 渐变太阳
  const sunGradient = `
    radial-gradient(circle at 50% 80%, 
      rgba(255,107,157,0.8) 0%, 
      rgba(255,154,111,0.6) 30%, 
      rgba(255,206,84,0.4) 60%, 
      transparent 100%
    )
  `;
  
  // 90s 颗粒质感
  const noiseOpacity = interpolate(random(`noise-${frame}`), [0, 1], [0.02, 0.08]);
  
  return (
    <AbsoluteFill style={{ background: '#1a1a2e' }}>
      {/* 80s 渐变背景 */}
      {filterType === '80s' && (
        <>
          <div
            style={{
              position: 'absolute',
              width: '100%',
              height: '60%',
              bottom: 0,
              background: sunGradient,
            }}
          />
          {/* 网格地面 */}
          <div
            style={{
              position: 'absolute',
              bottom: 0,
              width: '100%',
              height: '40%',
              background: `
                linear-gradient(90deg, rgba(255,107,157,0.3) 1px, transparent 1px),
                linear-gradient(rgba(255,107,157,0.3) 1px, transparent 1px)
              `,
              backgroundSize: '50px 50px',
              transform: 'perspective(500px) rotateX(60deg)',
              transformOrigin: 'bottom',
            }}
          />
        </>
      )}
      
      {/* VHS 扫描线 */}
      {filterType === 'vhs' && (
        <>
          <div
            style={{
              position: 'absolute',
              width: '100%',
              height: 2,
              top: scanlineY,
              background: 'rgba(255,255,255,0.1)',
            }}
          />
          <div
            style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              background: `repeating-linear-gradient(
                0deg,
                transparent,
                transparent 2px,
                rgba(0,0,0,0.1) 2px,
                rgba(0,0,0,0.1) 4px
              )`,
            }}
          />
          {/* RGB 漂移 */}
          <div
            style={{
              position: 'absolute',
              left: vhsOffset,
              width: '100%',
              height: '100%',
              mixBlendMode: 'screen',
              opacity: 0.3,
            }}
          >
            <div
              style={{
                width: '100%',
                height: '100%',
                background: 'rgba(255,0,0,0.1)',
              }}
            />
          </div>
        </>
      )}
      
      {/* 90s 颗粒效果 */}
      {filterType === '90s' && (
        <div
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            background: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
            opacity: noiseOpacity,
            mixBlendMode: 'overlay',
          }}
        />
      )}
      
      {/* 文字 */}
      <div
        style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: `translate(-50%, -50%) scale(${textProgress})`,
          fontSize: 120,
          fontWeight: 'bold',
          color: filterType === '80s' ? color : '#fff',
          textShadow: filterType === '80s' 
            ? `0 0 20px ${color}, 0 0 40px ${color}`
            : '2px 2px 0 rgba(255,0,255,0.5)',
          fontFamily: filterType === '90s' ? 'Impact, sans-serif' : 'Arial, sans-serif',
          letterSpacing: filterType === 'vhs' ? 10 : 0,
        }}
      >
        {text}
      </div>
      
      {/* 边框装饰（80s） */}
      {filterType === '80s' && (
        <div
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            width: width * 0.8,
            height: height * 0.8,
            border: '4px solid',
            borderImage: `linear-gradient(135deg, ${color}, #ff6b35, #ffd700) 1`,
          }}
        />
      )}
    </AbsoluteFill>
  );
};

// ============================================================================
// #120 形态变换增强版（Morphing）
// ============================================================================

interface MorphShape {
  startPath: string;
  endPath: string;
  color: string;
  delay: number;
}

const MorphShape: React.FC<{
  startPath: string;
  endPath: string;
  color: string;
  delay: number;
}> = ({ startPath, endPath, color, delay }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  
  const progress = spring({
    frame: frame - delay,
    fps,
    config: { damping: 20, stiffness: 50 },
  });
  
  // 简化的形态变换：通过 scale 和 rotation 模拟
  const scale = interpolate(progress, [0, 0.5, 1], [0, 1.2, 1]);
  const rotation = interpolate(progress, [0, 1], [0, 360]);
  
  return (
    <div
      style={{
        position: 'absolute',
        width: 200,
        height: 200,
        transform: `scale(${scale}) rotate(${rotation}deg)`,
        opacity: interpolate(progress, [0, 0.2, 1], [0, 1, 1]) as number,
      }}
    >
      <svg viewBox="0 0 100 100" style={{ width: '100%', height: '100%' }}>
        <path
          d={String(interpolate(progress as any, [0, 1], [startPath, endPath] as any, {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          }))}
          fill={color}
          opacity={0.8}
        />
      </svg>
    </div>
  );
};

export const AdvancedMorphing: React.FC<{
  text?: string;
  backgroundColor?: string;
}> = ({
  text = 'MORPH',
  backgroundColor = '#0a0a1a',
}) => {
  const frame = useCurrentFrame();
  const { width, height, fps } = useVideoConfig();
  
  const textProgress = spring({
    frame: frame - 60,
    fps,
    config: { damping: 15 },
  });
  
  // 形状路径定义
  const shapes = [
    {
      startPath: 'M10,50 Q50,10 90,50 Q50,90 10,50', // 菱形
      endPath: 'M50,10 L90,50 L50,90 L10,50 Z', // 正方形
      color: '#6366f1',
      delay: 0,
    },
    {
      startPath: 'M50,10 A40,40 0 1,1 50,90 A40,40 0 1,1 50,10', // 圆形
      endPath: 'M10,10 L90,10 L90,90 L10,90 Z', // 方形
      color: '#ec4899',
      delay: 10,
    },
    {
      startPath: 'M50,10 L61,40 L95,40 L68,60 L79,90 L50,70 L21,90 L32,60 L5,40 L39,40 Z', // 星形
      endPath: 'M50,10 A40,40 0 1,1 50,90 A40,40 0 1,1 50,10', // 圆形
      color: '#f59e0b',
      delay: 20,
    },
  ];
  
  return (
    <AbsoluteFill style={{ background: backgroundColor }}>
      {/* 背景渐变 */}
      <div
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          background: 'radial-gradient(circle at center, rgba(99,102,241,0.2) 0%, transparent 70%)',
        }}
      />
      
      {/* 形态变换形状 */}
      {shapes.map((shape, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            left: width * (0.25 + i * 0.25) - 100,
            top: height * 0.35,
          }}
        >
          <MorphShape {...shape} />
        </div>
      ))}
      
      {/* 中心文字 */}
      <div
        style={{
          position: 'absolute',
          left: '50%',
          top: '70%',
          transform: `translate(-50%, -50%) translateY(${interpolate(textProgress, [0, 1], [50, 0])}px)`,
          opacity: textProgress,
          fontSize: 80,
          fontWeight: 'bold',
          color: '#fff',
          textShadow: '0 0 30px rgba(99,102,241,0.8)',
          fontFamily: 'Arial, sans-serif',
        }}
      >
        {text}
      </div>
    </AbsoluteFill>
  );
};
